import { ProductHeading } from '@issuu/silkscreen';
import escape from 'lodash.escape';
import React, { useContext } from 'react';
import styled from 'styled-components';

import DocumentContext from '../DocumentPage/DocumentContext';

const Wrapper = styled.div`
    overflow-wrap: break-word;
`;

const CoverImage = ({ pageNumber = 1 }: { pageNumber?: number }) => {
    const {
        document: { title, publicationId, revisionId },
        publisher: { displayName },
        hosts: { image: imageHost },
    } = useContext(DocumentContext);

    const documentId = `${revisionId}-${publicationId}`;
    return (
        <div itemScope itemType="http://schema.org/ImageObject">
            <meta content="true" itemProp="representativeOfPage" />
            <p className="content">
                <img
                    alt={`Page ${pageNumber}`}
                    itemProp="image"
                    src={`https://${imageHost}/${documentId}/jpg/page_${pageNumber}_thumb_large.jpg`}
                />
                <meta
                    content={`Page ${pageNumber} of ${title} by ${displayName}`}
                    itemProp="caption"
                />
                <meta
                    content={`https://${imageHost}/${documentId}/jpg/page_${pageNumber}_thumb_large.jpg`}
                    itemProp="contentUrl"
                />
            </p>
        </div>
    );
};

const Paragraph: React.FC<{ text: string }> = ({ text }) => <p>{escape(text)}</p>;

const Page: React.FC<{ text: string; pageNumber: number }> = ({ text, pageNumber }) => (
    <>
        <section aria-label={`Page ${pageNumber}`}>
            {text.split(/\n\n+/).map((text, index) => (
                <Paragraph key={`Page:${index}`} text={text.trim()} />
            ))}
        </section>
        <hr />
    </>
);

const ReaderText: React.FC<{ pageTexts: string[]; pageNumber?: number }> = ({
    pageTexts,
    pageNumber,
}) => {
    const startPageNumber = pageNumber || 1;
    const pages = pageNumber ? [pageTexts[pageNumber - 1]] : pageTexts;
    return (
        <main itemProp="text" style={{ overflowWrap: 'break-word', maxWidth: '100%' }}>
            {pages.map((pageText, index) => (
                <Page
                    key={`ReaderText:${index}`}
                    pageNumber={index + startPageNumber}
                    text={pageText}
                />
            ))}
        </main>
    );
};

const Navigation = ({ pageNumber }: { pageNumber?: number }) => {
    const {
        document: { pageCount, documentName },
        publisher: { username },
    } = useContext(DocumentContext);
    const hrefPrefix = `/${username}/docs/${documentName}/`;
    return (
        <nav>
            {pageNumber ? (
                <div>
                    {pageNumber !== 1 ? (
                        <a href={`${hrefPrefix}${pageNumber - 1}`} key="prev">
                            Previous page
                        </a>
                    ) : null}{' '}
                    {pageNumber < pageCount ? (
                        <a href={`${hrefPrefix}${pageNumber + 1}`} key="next">
                            Next page
                        </a>
                    ) : null}
                </div>
            ) : null}
            <ul>
                {Array.from(Array(pageCount), (_, index) => (
                    <li
                        key={`Navigation:${index}`}
                        style={{
                            display: 'inline-block',
                            textAlign: 'right',
                            minWidth: '2.5rem',
                            minHeight: '1.5rem',
                            padding: '0.75rem',
                        }}
                    >
                        <a
                            href={
                                pageNumber === index + 1 ? undefined : `${hrefPrefix}${index + 1}`
                            }
                        >
                            {index + 1}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

export const TextVersion: React.FC = () => {
    const {
        crawler,
        document: { title },
    } = useContext(DocumentContext);
    if (!crawler) return null;
    const { pageTexts, pageNumber } = crawler;
    return (
        <Wrapper>
            <ProductHeading level={1}>{title}</ProductHeading>
            <CoverImage pageNumber={pageNumber} />
            <ReaderText pageNumber={pageNumber} pageTexts={pageTexts} />
            <Navigation pageNumber={pageNumber} />
        </Wrapper>
    );
};
